/** FOR-41. Browser → S3 direct upload via a presigned PUT. The server re-verifies size/type on finalize. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export type UploadTicket = {uploadUrl: string; storageKey: string; contentType: string; expiresAt: string};
export type UploadRef = {storageKey: string; fileName: string; sizeBytes: number};

/**
 * Asks the server for a presigned PUT, then sends the file straight to the bucket.
 *
 * The 50 MB check here is a courtesy that saves a doomed round trip; the server enforces
 * the real limit when the asset is created from the returned ref. A refusal from
 * `requestTicket` (e.g. uploads unavailable) propagates unchanged; anything that goes
 * wrong on the PUT itself rejects with Error('UPLOAD_FAILED').
 */
export async function uploadClientAssetFile(args: {
	projectId: string;
	file: File;
	requestTicket: (input: {projectId: string; fileName: string; sizeBytes: number}) => Promise<UploadTicket>;
}): Promise<UploadRef> {
	const {projectId, file, requestTicket} = args;
	if (file.size > MAX_UPLOAD_BYTES) throw new Error('FILE_TOO_LARGE');
	const ticket = await requestTicket({projectId, fileName: file.name, sizeBytes: file.size});
	let res: Response;
	try {
		// Content-Type MUST equal the signed value and is the only header sent; content-length
		// is set by the browser from the body. No credentials: the signature is the auth.
		res = await fetch(ticket.uploadUrl, {method: 'PUT', headers: {'Content-Type': ticket.contentType}, body: file});
	} catch {
		throw new Error('UPLOAD_FAILED');
	}
	if (!res.ok) throw new Error('UPLOAD_FAILED');
	return {storageKey: ticket.storageKey, fileName: file.name, sizeBytes: file.size};
}
